import type { BoardDoc } from '../doc/BoardDoc';
import { clearFailed, resendUnsent, useUploads } from '../sync/uploadQueue';
import { referencedAssetIds } from './storage';
import { useBoard, useBoardMeta, useItemsVersion, usePane, useSyncState } from './context';

const LABELS = {
  connecting: 'connecting',
  synced: 'synced',
  syncing: 'syncing',
  offline: 'offline',
  error: 'sync error',
} as const;

export function StatusBar() {
  const board = useBoard();
  const meta = useBoardMeta();
  useItemsVersion();
  const selected = usePane((s) => s.selection.size);
  const sync = useSyncState();
  const uploads = useUploads((s) => s.pending);
  const failed = useUploads((s) => s.failed);
  const failedWhy = useUploads((s) => s.lastError);
  const failedText = `The server refused ${failed === 1 ? 'an upload' : 'some uploads'}${failedWhy ? `: ${failedWhy}` : ''}. Those images show on this device only; they are sent again the next time the board opens.`;

  return (
    <div className="statusbar">
      <span className="statusbar-title">{meta.title || board.boardId}</span>
      <span className="statusbar-items">{board.index.size} items</span>
      <span className="statusbar-selected">{selected > 0 ? `${selected} selected` : ''}</span>
      {uploads > 0 && <span>{uploads} uploading</span>}
      {failed > 0 && (
        <button type="button" className="statusbar-failed bad" title={failedText} onClick={() => retryFailed(board, failedText)}>
          {failed} upload{failed === 1 ? '' : 's'} failed
        </button>
      )}
      {board.remote && (
        <span
          className={`sync sync-${sync.status}`}
          title={sync.status === 'error' && sync.error ? `Sync error: ${sync.error}` : (sync.error ?? undefined)}
        >
          <i />
          <span className="sync-label">
            {sync.role === 'viewer' ? 'view only' : LABELS[sync.status]}
            {sync.status === 'error' && sync.error ? `: ${sync.error}` : ''}
          </span>
        </span>
      )}
      {!board.remote && (
        <span className="sync sync-local">
          <i />
          <span className="sync-label">local only</span>
        </span>
      )}
    </div>
  );
}

/** Tap or click: the reason in full (a touch screen has no tooltip), and the chance to send again now. */
function retryFailed(board: BoardDoc, text: string): void {
  if (!board.remote) {
    window.alert(text);
    return;
  }
  if (!window.confirm(`${text}

Send this board's unsent images again now?`)) return;
  clearFailed();
  void resendUnsent(board.boardId, referencedAssetIds(board));
}
